import {
  type PanelPlacement,
  type PanelPresenter,
  CenterModalPanel,
  SideSheetPanel,
  presenterKindForPlacement,
} from './panel-presenter'
import {
  SubmitIconSvg,
  applyPanelCornerRadius,
  applyShellInputAnimation,
  applyThemeVariables,
} from './sheet-shell-shared'
import {Shell, type ShellOptions} from './shell-base'
import {ShellStyles} from './shell-styles'
import {
  SuggestionOrderValues,
  type AnagramAutoSubmitMetadata,
  type AnagramMessage,
  type EmbeddedToSheetConfig,
  type EnvironmentConfig,
  type ShellConfig,
} from './types'

declare const __ETS_CSS__: string
const EtsStyles: string = typeof __ETS_CSS__ !== 'undefined' ? __ETS_CSS__ : ''

declare const __BITS_CSS__: string
const BitsStyles: string = typeof __BITS_CSS__ !== 'undefined' ? __BITS_CSS__ : ''

type PendingAutoSubmit = {
  text: string
  metadata?: AnagramAutoSubmitMetadata
}

export class EmbeddedToSheetShell extends Shell {
  private shellConfig: EmbeddedToSheetConfig
  private readonly scriptElement: HTMLScriptElement
  private container: HTMLDivElement | null = null
  private inlineRoot: HTMLDivElement | null = null
  private sheetHost: HTMLDivElement | null = null
  private sheetWrapper: HTMLDivElement | null = null
  private presenter!: PanelPresenter
  private currentIframe: HTMLIFrameElement | null = null
  private currentIframeContextKey: string | null = null
  private isOpen = false
  private isIframeReady = false
  private pendingAutoSubmit: PendingAutoSubmit | null = null
  private pendingFocusInput = false
  private viewportResizeListener: (() => void) | null = null

  constructor(
    config: EnvironmentConfig,
    experienceId: string,
    shellConfig: EmbeddedToSheetConfig,
    scriptElement: HTMLScriptElement,
    options?: ShellOptions
  ) {
    super(config, experienceId, options)
    this.shellConfig = shellConfig
    this.scriptElement = scriptElement
  }

  mount(): void {
    const fontFamily = this.getShellFontStack(this.shellConfig.fontFamily)

    this.container = document.createElement('div')
    this.styleAsInlineHost(this.container)
    const shadow = this.container.attachShadow({mode: 'open'})
    this.attachFontStylesheet(shadow, this.shellConfig.fontFamily)

    const style = document.createElement('style')
    style.textContent = ShellStyles + EtsStyles
    shadow.appendChild(style)

    const root = document.createElement('div')
    root.className = 'anagram-ets-root'
    root.style.fontFamily = fontFamily

    applyThemeVariables(root, this.shellConfig.theme)
    this.inlineRoot = root

    const inputWrapper = document.createElement('div')
    inputWrapper.className = 'anagram-ets-input-wrapper'

    applyShellInputAnimation(inputWrapper, this.shellConfig.animationStyle)

    if (this.shellConfig.showInputShadow) {
      const topShadow = document.createElement('div')
      topShadow.className = 'anagram-ets-crescent-shadow anagram-ets-crescent-shadow-top'
      inputWrapper.appendChild(topShadow)

      const bottomShadow = document.createElement('div')
      bottomShadow.className = 'anagram-ets-crescent-shadow anagram-ets-crescent-shadow-bottom'
      inputWrapper.appendChild(bottomShadow)
    }

    const form = document.createElement('form')
    form.className = 'anagram-ets-input'

    const textInput = document.createElement('input')
    textInput.type = 'text'
    textInput.className = 'anagram-ets-input-field'
    textInput.placeholder = this.shellConfig.placeholderText

    const submitBtn = document.createElement('button')
    submitBtn.type = 'submit'
    submitBtn.className = 'anagram-ets-input-icon'
    submitBtn.innerHTML = SubmitIconSvg
    submitBtn.setAttribute('aria-label', 'Submit')

    form.appendChild(textInput)
    form.appendChild(submitBtn)

    form.addEventListener('submit', (e) => {
      e.preventDefault()
      const text = textInput.value.trim()
      if (text) {
        this.pendingAutoSubmit = {text}
        textInput.value = ''
      } else {
        this.pendingFocusInput = true
      }

      if (this.isOpen) {
        if (this.isIframeReady) {
          this.flushPendingActions()
        }
      } else {
        this.openSheet()
      }
    })
    inputWrapper.appendChild(form)
    root.appendChild(inputWrapper)
    this.renderInlineStartingState(root)

    shadow.appendChild(root)

    this.sheetHost = document.createElement('div')
    this.styleAsViewportOverlay(this.sheetHost)
    document.body.appendChild(this.sheetHost)

    const sheetShadow = this.sheetHost.attachShadow({mode: 'open'})
    this.attachFontStylesheet(sheetShadow, this.shellConfig.fontFamily)

    const sheetStyle = document.createElement('style')
    sheetStyle.textContent = ShellStyles + EtsStyles + BitsStyles
    sheetShadow.appendChild(sheetStyle)

    const sheetWrapper = document.createElement('div')
    sheetWrapper.className = 'anagram-root'
    sheetWrapper.style.fontFamily = fontFamily
    applyPanelCornerRadius(sheetWrapper, this.shellConfig.theme['--radius'])
    sheetShadow.appendChild(sheetWrapper)
    this.sheetWrapper = sheetWrapper

    this.presenter = this.createPresenter(this.shellConfig.panelLocation)
    this.presenter.mount(sheetWrapper)

    if (this.scriptElement.parentNode) {
      this.scriptElement.parentNode.insertBefore(this.container, this.scriptElement)
    }

    this.startListeningForIframeMessages()
    this.syncViewportListener()
  }

  override applyPreviewShellConfig(config: ShellConfig): boolean {
    if (config.formFactor !== 'embedded-to-sheet' || !this.container) {
      return false
    }

    this.shellConfig = config as EmbeddedToSheetConfig
    this.updateActivePreviewConfig(config)
    if (this.sheetWrapper) {
      applyPanelCornerRadius(this.sheetWrapper, this.shellConfig.theme['--radius'])
    }

    if (this.inlineRoot) {
      applyThemeVariables(this.inlineRoot, this.shellConfig.theme)
      this.renderInlineStartingState(this.inlineRoot)
      const textInput = this.inlineRoot.querySelector('.anagram-ets-input-field')
      if (textInput instanceof HTMLInputElement) {
        textInput.placeholder = this.shellConfig.placeholderText
      }
      const inputWrapper = this.inlineRoot.querySelector('.anagram-ets-input-wrapper')
      if (inputWrapper instanceof HTMLElement) {
        applyShellInputAnimation(inputWrapper, this.shellConfig.animationStyle)
      }
    }

    this.swapPresenterIfNeeded(this.shellConfig.panelLocation)
    this.presenter.applyPlacement(this.shellConfig.panelLocation)

    if (this.isOpen) {
      const {parentUrl, pageTitle} = this.getLiveIframePageContext()
      this.currentIframeContextKey = null
      this.ensureIframe(parentUrl, pageTitle)
    }

    return true
  }

  private createPresenter(placement: PanelPlacement): PanelPresenter {
    const callbacks = {
      onDismissRequested: () => this.closeSheet(),
      onClosed: () => {
        this.isOpen = false
      },
    }

    if (presenterKindForPlacement(placement) === 'center-modal') {
      return new CenterModalPanel({
        fontFamily: this.getShellFontStack(this.shellConfig.fontFamily),
        theme: this.shellConfig.theme,
        callbacks,
      })
    }

    return new SideSheetPanel({
      fontFamily: this.getShellFontStack(this.shellConfig.fontFamily),
      placement,
      mobileDrawer: true,
      callbacks,
    })
  }

  private swapPresenterIfNeeded(placement: PanelPlacement): boolean {
    if (!this.sheetWrapper || presenterKindForPlacement(placement) === this.presenter.kind) {
      return false
    }

    const wasOpen = this.isOpen
    this.presenter.destroy()
    this.currentIframe = null
    this.currentIframeContextKey = null
    this.isIframeReady = false
    this.isOpen = false

    this.presenter = this.createPresenter(placement)
    this.presenter.mount(this.sheetWrapper)
    this.syncViewportListener()

    if (wasOpen) {
      this.openSheet()
    }

    return true
  }

  private syncViewportListener(): void {
    if (this.presenter.tracksViewport && !this.viewportResizeListener) {
      this.viewportResizeListener = () => this.presenter.syncViewport()
      window.addEventListener('resize', this.viewportResizeListener)
      window.visualViewport?.addEventListener('resize', this.viewportResizeListener, {passive: true})
      window.visualViewport?.addEventListener('scroll', this.viewportResizeListener, {passive: true})
      return
    }

    if (!this.presenter.tracksViewport && this.viewportResizeListener) {
      this.teardownViewportListener()
    }
  }

  private teardownViewportListener(): void {
    if (!this.viewportResizeListener) {
      return
    }
    window.removeEventListener('resize', this.viewportResizeListener)
    window.visualViewport?.removeEventListener('resize', this.viewportResizeListener)
    window.visualViewport?.removeEventListener('scroll', this.viewportResizeListener)
    this.viewportResizeListener = null
  }

  private renderInlineStartingState(root: HTMLDivElement): void {
    for (const child of Array.from(root.children)) {
      if (child.classList.contains('anagram-ets-message') || child.classList.contains('anagram-ets-suggestions')) {
        child.remove()
      }
    }

    const inputWrapper = root.querySelector('.anagram-ets-input-wrapper')
    const startingState = this.shellConfig.startingState
    const inputFirst = this.shellConfig.suggestionOrder === SuggestionOrderValues.InputFirst

    const insertRelativeToInput = (node: HTMLElement, position: 'before' | 'after'): void => {
      if (position === 'before') {
        if (inputWrapper) {
          root.insertBefore(node, inputWrapper)
        } else {
          root.appendChild(node)
        }
        return
      }

      if (inputWrapper?.nextSibling) {
        root.insertBefore(node, inputWrapper.nextSibling)
      } else {
        root.appendChild(node)
      }
    }

    if (startingState.text) {
      const message = document.createElement('div')
      message.className = 'anagram-ets-message'
      message.textContent = startingState.text
      insertRelativeToInput(message, 'before')
    }

    if (startingState.options && startingState.options.length > 0) {
      const suggestions = document.createElement('div')
      suggestions.className = 'anagram-ets-suggestions'
      for (const option of startingState.options) {
        const btn = document.createElement('button')
        btn.className = 'anagram-ets-suggestion'
        btn.textContent = option
        btn.addEventListener('click', () => {
          this.pendingAutoSubmit = {text: option, metadata: {source: 'suggestion'}}
          if (this.isOpen && this.isIframeReady) {
            this.flushPendingActions()
            return
          }
          this.openSheet()
        })
        suggestions.appendChild(btn)
      }

      insertRelativeToInput(suggestions, inputFirst ? 'after' : 'before')
    }
  }

  override destroy(): void {
    this.teardownViewportListener()
    this.presenter?.destroy()
    this.currentIframe?.remove()
    this.currentIframe = null
    this.currentIframeContextKey = null
    this.isOpen = false
    this.isIframeReady = false
    this.pendingAutoSubmit = null
    this.pendingFocusInput = false
    this.container?.remove()
    this.container = null
    this.inlineRoot = null
    this.sheetHost?.remove()
    this.sheetHost = null
    this.sheetWrapper = null

    super.destroy()
  }

  protected override ownsIframeMessageSource(source: MessageEventSource): boolean {
    return source === this.currentIframe?.contentWindow
  }

  private openSheet(): void {
    if (this.isOpen) return
    this.isOpen = true

    const {parentUrl, pageTitle} = this.getLiveIframePageContext()
    this.ensureIframe(parentUrl, pageTitle)

    if (this.isIframeReady) {
      this.presenter.showReady()
      this.flushPendingActions()
    }

    this.presenter.open()
  }

  private closeSheet(): void {
    if (!this.isOpen) return
    this.pendingAutoSubmit = null
    this.pendingFocusInput = false
    this.presenter.close()
  }

  protected onIframeReady(): void {
    this.isIframeReady = true
    this.presenter.showReady()
    this.flushPendingActions()
  }

  private ensureIframe(parentUrl: string, pageTitle: string): void {
    const contextKey = this.buildIframeContextKey(parentUrl, pageTitle)
    if (this.currentIframe && this.currentIframeContextKey === contextKey) {
      return
    }

    this.currentIframe?.remove()

    const iframeUrl = this.createIframeUrl('sheet')
    iframeUrl.searchParams.append('parentUrl', parentUrl)
    if (pageTitle) iframeUrl.searchParams.append('pageTitle', pageTitle)

    this.currentIframe = this.createIframe(iframeUrl)
    this.currentIframeContextKey = contextKey
    this.isIframeReady = false
    this.presenter.showLoading()
    this.presenter.iframeContainer.appendChild(this.currentIframe)
  }

  private flushPendingActions(): void {
    if (this.pendingAutoSubmit && this.currentIframe?.contentWindow) {
      this.currentIframe.contentWindow.postMessage(
        {
          type: 'ANAGRAM_AUTO_SUBMIT',
          text: this.pendingAutoSubmit.text,
          ...(this.pendingAutoSubmit.metadata ? {metadata: this.pendingAutoSubmit.metadata} : {}),
        },
        '*'
      )
    }

    if (this.pendingFocusInput && this.currentIframe?.contentWindow) {
      this.currentIframe.contentWindow.postMessage({type: 'ANAGRAM_FOCUS_INPUT'}, '*')
    }

    this.pendingAutoSubmit = null
    this.pendingFocusInput = false
  }

  protected handleShellSpecificIframeMessage(msg: AnagramMessage): void {
    switch (msg.type) {
      case 'ANAGRAM_CLOSE_REQUESTED': {
        this.closeSheet()
        break
      }
      case 'ANAGRAM_RESIZE': {
        const nextHeight = Number(msg.height)
        if (!Number.isNaN(nextHeight) && nextHeight > 0) {
          this.presenter.setBodyHeight(nextHeight)
        }
        break
      }
      case 'ANAGRAM_START_OVER': {
        this.presenter.resetBodyHeight()
        break
      }
    }
  }
}
